import { fileExtension } from '../composables/utils';
import axios from 'axios';

export const imagePlaceholderSrc = (width = 160, height = 90) => {
    const svgWidth = parseInt(width) || 160;
    const svgHeight = parseInt(height) || 90;
    const iconWidth = Math.min(56, Math.max(32, svgWidth * 0.35));
    const iconHeight = iconWidth * 0.75;
    const iconX = (svgWidth - iconWidth) / 2;
    const iconY = (svgHeight - iconHeight) / 2;
    const sunX = iconX + iconWidth * 0.3;
    const sunY = iconY + iconHeight * 0.34;
    const sunR = Math.max(3, iconWidth * 0.09);
    const strokeWidth = Math.max(3, Math.min(5, iconWidth * 0.09));

    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svgWidth} ${svgHeight}" role="img" aria-label="Image placeholder">
    <rect width="${svgWidth}" height="${svgHeight}" fill="#f3f4f6"/>
    <rect x="${iconX}" y="${iconY}" width="${iconWidth}" height="${iconHeight}" fill="none" stroke="#9ca3af" stroke-width="${strokeWidth}"/>
    <circle cx="${sunX}" cy="${sunY}" r="${sunR}" fill="#9ca3af"/>
    <path d="M${iconX + iconWidth * 0.11} ${iconY + iconHeight * 0.81}l${iconWidth * 0.27} -${iconHeight * 0.33} ${iconWidth * 0.21} ${iconHeight * 0.26} ${iconWidth * 0.13} -${iconHeight * 0.17} ${iconWidth * 0.21} ${iconHeight * 0.24}" fill="none" stroke="#9ca3af" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`.trim();

    return `data:image/svg+xml;base64,${btoa(svg)}`;
};

export const dropFileFromInput = (dataPack, fileInputId = '#dropzone-file') => {
    const image = document.querySelector(fileInputId).files[0];

    if (
        typeof image === 'object' &&
        ['png', 'jpg', 'jpeg', 'gif'].includes(fileExtension(image.name).toLowerCase())
    ) {
        // Setup load end event
        const reader = new FileReader();
        reader.onloadend = () => {
            // Convert to Base64 string
            const base64 = reader.result;
            dataPack.name = image.name;
            dataPack.type = image.type;
            dataPack.image = base64;
            dataPack.result = base64;
            if (typeof dataPack.cropper === 'object') {
                dataPack.cropper.replace(base64);
            }
        };
        reader.readAsDataURL(image);
    }
};

export const dropFileFromPath = async (dataPack, inline = false) => {
    // Get the remote image as a Blob with the fetch API
    if (inline === false) {
        dataPack.image = dataPack.http;
        dataPack.result = dataPack.http;
        dataPack.status = true;
    } else {
        const source = axios.CancelToken.source();
        const timeoutId = setTimeout(() => {
            source.cancel();
        }, 7000);

        await axios
            .get(dataPack.http, {
                responseType: 'blob',
                cancelToken: source.token,
            })
            .then(function (response) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    // Convert to Base64 string
                    const base64 = reader.result;
                    dataPack.name = '';
                    dataPack.type = response.data.type;
                    dataPack.image = base64;
                    dataPack.result = base64;
                    if (typeof dataPack.cropper === 'object') {
                        dataPack.cropper.replace(base64);
                    }

                    dataPack.status = true;
                };
                reader.readAsDataURL(response.data);
            });
        clearTimeout(timeoutId);
    }
};

export const insertImageFromStorage = async (dataPack, imagePath) => {
    await dropFileFromPath(dataPack, imagePath);
};

export const crop = (dataPack) => {
    dataPack.result = dataPack.cropper.getCroppedCanvas().toDataURL();
};

export const flipH = (dataPack) => {
    let scale = dataPack.flipX;
    scale = scale ? -scale : -1;
    dataPack.cropper.scaleX(scale);
    dataPack.flipX = scale;
};

export const flipV = (dataPack) => {
    let scale = dataPack.flipY;
    scale = scale ? -scale : -1;
    dataPack.cropper.scaleY(scale);
    dataPack.flipY = scale;
};

export const move = (dataPack, offsetX, offsetY) => {
    dataPack.cropper.move(offsetX, offsetY);
};

export const rotate = (dataPack, deg) => {
    dataPack.cropper.rotate(deg);
};

export const zoom = (dataPack, percent) => {
    dataPack.cropper.relativeZoom(percent);
};

export const reset = (dataPack) => {
    dataPack.cropper.reset();
};
export const resize = async (dataPack, maxWidth, maxHeight) => {
    const baseImageObj = new Image();
    baseImageObj.src = dataPack.result;

    return new Promise((resolve) => {
        baseImageObj.onload = () => {
            const imageWidth = baseImageObj.naturalWidth;
            const imageHeight = baseImageObj.naturalHeight;
            const imageAspect = imageWidth / imageHeight;
            let newWidth = imageWidth;
            let newHeight = imageHeight;

            if (imageAspect >= 1) {
                newWidth = imageWidth > maxWidth ? maxWidth : newWidth;
                newHeight = Math.ceil(newWidth / imageAspect);
            } else {
                newHeight = imageHeight > maxHeight ? maxHeight : newHeight;
                newWidth = Math.ceil(newHeight * imageAspect);
            }

            const canvas = document.createElement('canvas');
            canvas.width = newWidth;
            canvas.height = newHeight;

            const context = canvas.getContext('2d');
            context.clearRect(0, 0, newWidth, newHeight);
            context.drawImage(baseImageObj, 0, 0, newWidth, newHeight);

            // Get data back to base64
            dataPack.result = canvas.toDataURL(dataPack.type);

            // Resolve the promise with the updated dataPack
            resolve(dataPack);
        };
    });
};

export const drawText = (canvasContext, text) => {
    canvasContext.font = text.font || '48px sans-serif';
    const x = text.x || 10,
        y = text.y || 50;
    canvasContext.strokeText(text.content, x, y);
};

export const useImage = {
    imagePlaceholderSrc: imagePlaceholderSrc,
    dropFileFromInput: dropFileFromInput,
    insertImageFromStorage: insertImageFromStorage,
    drawText: drawText,
    crop: crop,
    flipH: flipH,
    flipV: flipV,
    move: move,
    rotate: rotate,
    zoom: zoom,
    reset: reset,
    resize: resize,
};

export default useImage;
